import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Frequency } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { FrequencyForm } from "../FrequencyForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListFrequencies.scss";

const frequencyController = new Frequency();

export function ListFrequencies({ frequenciesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [frequencies,       setFrequencies]       = useState(null);
  const [frequenciesFilter, setFrequenciesFilter] = useState([]);
  const [search,            setSearch]            = useState("");
  const [showModal,         setShowModal]         = useState(false);
  const [selectedFreq,      setSelectedFreq]      = useState(null);
  const [showConfirm,       setShowConfirm]       = useState(false);
  const [confirmMsg,        setConfirmMsg]        = useState("");
  const [isDelete,          setIsDelete]          = useState(false);
  const [rowMenu,           setRowMenu]           = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".frequencies-list__row-menu, .frequencies-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setFrequencies(null);
        const res = await frequencyController.getFrequencies(accessToken, frequenciesActive);
        setFrequencies(res);
        setFrequenciesFilter(res);
      } catch {
        setFrequencies([]);
        setFrequenciesFilter([]);
      }
    })();
  }, [frequenciesActive, reload]);

  useEffect(() => {
    if (!frequencies) return;
    if (!search) { setFrequenciesFilter(frequencies); return; }
    const q = search.toLowerCase();
    setFrequenciesFilter(
      frequencies.filter((f) =>
        f.code?.toLowerCase().includes(q) || f.name?.toLowerCase().includes(q)
      )
    );
  }, [search, frequencies]);

  const openCreate = () => { setSelectedFreq(null); setShowModal(true); };
  const openEdit   = (f)  => { setSelectedFreq(f);   setShowModal(true); };

  const openToggle = (f) => {
    setSelectedFreq(f);
    setIsDelete(false);
    setConfirmMsg(`¿${f.active ? t("deactivate") : t("activate")} "${f.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (f) => {
    setSelectedFreq(f);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${f.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await frequencyController.updateFrequency(accessToken, selectedFreq._id, { active: !selectedFreq.active });
      toast.success(selectedFreq.active ? t("frequency_deactivated") : t("frequency_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await frequencyController.deleteFrequency(accessToken, selectedFreq._id);
      toast.success(t("frequency_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (frequencies === null) return <LoadingSpinner />;

  return (
    <div className="frequencies-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="frequencies-list__card">
        <div className="frequencies-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-frequency-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {frequenciesFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="frequencies-list__tbl-scroll">
            <Table className="frequencies-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {frequenciesFilter.map((f) => (
                  <Table.Row key={f._id}>
                    <Table.Cell data-label={t("code")}>{f.code ? f.code.toUpperCase() : ""}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{f.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="frequencies-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="frequencies-list__row-menu">
                        <button type="button" className="frequencies-list__row-menu-btn" onClick={(e) => toggleRowMenu(f._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === f._id && ReactDOM.createPortal(
                        <div
                          className="frequencies-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(f); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(f); setRowMenu(null); }}>
                            <Icon name={f.active ? "ban" : "check"} />{f.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="frequencies-list__row-menu-item--danger" onClick={() => { openDelete(f); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedFreq ? `${t("edit")}: ${selectedFreq.name}` : t("new_frequency")}
        size="small"
        className="catalog-modal"
      >
        <FrequencyForm
          frequency={selectedFreq}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedFreq?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
